import { useMemo, useState } from "react";
import { FaChalkboardTeacher, FaChevronLeft, FaChevronRight, FaStickyNote } from "react-icons/fa";
import { langs, langOf } from "../../../helpers/langs";
import { trialContacts } from "../../../helpers/trialPhone";
import BotLinkButton from "../../../components/BotLinkButton/BotLinkButton";
import { DAY, dateToYmd, kyivParts, nextTrialDate, shortDate, trialDateOf, weekDays, weekIndex, ymdToDate } from "../../../helpers/trialDate";
import * as styled from "./TrialDays.styled";

// Пробні по днях тижня. Дата пробного — lessonDate з бекенду або (старі записи) порахована від дати створення, див. helpers/trialDate.js

const dayShort = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];
const dayLong = ["Понеділок", "Вівторок", "Середа", "Четвер", "Пʼятниця", "Субота", "Неділя"];
const mondayOf = (date) => new Date(date.getTime() - weekIndex(date) * DAY);
// З цієї дати бекенд запамʼятовує вхід пробного (lastSeenAt); про раніше створені пробні даних немає
const SEEN_TRACKED_SINCE = "2026-11-04";

function TrialDays({ lessons, accessType, statusOptions, getStatusColor, onStatusChange }) {
  const today = kyivParts(new Date());
  const todayDate = ymdToDate(today.ymd);
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayDate));
  const [lang, setLang] = useState("");
  const isAll = accessType === "all";

  const entries = useMemo(
    () =>
      lessons
        .filter((lesson) => (isAll ? !lang || lesson.accessType === lang : lesson.accessType === accessType))
        .flatMap((lesson) =>
          (lesson.classSchedule || []).map(({ day, time }, idx) => ({
            key: `${lesson._id}-${idx}`,
            lesson,
            time: time || "",
            date: idx === 0 ? trialDateOf(lesson) : nextTrialDate(day, time, lesson.createdAt),
          }))
        )
        .filter(({ date }) => date)
        .sort((a, b) => a.time.localeCompare(b.time)),
    [lessons, isAll, lang, accessType]
  );

  const byDate = useMemo(() => {
    const map = {};
    entries.forEach((entry) => (map[entry.date] = map[entry.date] || []).push(entry));
    return map;
  }, [entries]);

  // Урок уже минув, а статус лишився «Запланований» — треба відмітити
  const unmarked = entries
    .filter(
      ({ date, time, lesson }) =>
        (lesson.lessonStatus || "scheduled") === "scheduled" &&
        (date < today.ymd || (date === today.ymd && time < today.hm))
    )
    .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));

  const week = weekDays.map((_, i) => {
    const date = new Date(weekStart.getTime() + i * DAY);
    const ymd = dateToYmd(date);
    return { i, date, ymd, items: byDate[ymd] || [] };
  });
  const weekTotal = week.reduce((sum, { items }) => sum + items.length, 0);
  const weekEnd = new Date(weekStart.getTime() + 6 * DAY);
  const isThisWeek = dateToYmd(weekStart) === dateToYmd(mondayOf(todayDate));
  const moveWeek = (n) => setWeekStart((prev) => new Date(prev.getTime() + n * 7 * DAY));

  const jumpTo = (ymd) => {
    setWeekStart(mondayOf(ymdToDate(ymd)));
    setTimeout(() => document.getElementById(`trial-day-${ymd}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const status = (lesson) => statusOptions.find((opt) => opt.value === (lesson.lessonStatus || "scheduled"));

  return (
    <styled.Wrapper>
      <styled.Toolbar>
        <styled.WeekNav>
          <styled.NavButton onClick={() => moveWeek(-1)} title="Попередній тиждень"><FaChevronLeft /></styled.NavButton>
          <styled.WeekTitle>
            {shortDate(weekStart)} — {shortDate(weekEnd)}
            <span>{weekTotal ? `${weekTotal} пробн.` : "пробних немає"}</span>
          </styled.WeekTitle>
          <styled.NavButton onClick={() => moveWeek(1)} title="Наступний тиждень"><FaChevronRight /></styled.NavButton>
          {!isThisWeek && <styled.TodayButton onClick={() => setWeekStart(mondayOf(todayDate))}>Цей тиждень</styled.TodayButton>}
        </styled.WeekNav>

        {isAll && (
          <styled.Chips>
            <styled.Chip active={!lang} onClick={() => setLang("")}>Всі мови</styled.Chip>
            {langs.map(({ key, value }) => (
              <styled.Chip key={key} active={lang === key} onClick={() => setLang(key)}>{value}</styled.Chip>
            ))}
          </styled.Chips>
        )}
      </styled.Toolbar>

      <styled.DayStrip>
        {week.map(({ i, date, ymd, items }) => (
          <styled.DayTile
            key={ymd}
            isToday={ymd === today.ymd}
            isPast={ymd < today.ymd}
            empty={!items.length}
            onClick={() => items.length && jumpTo(ymd)}
          >
            <styled.TileDay>{dayShort[i]}</styled.TileDay>
            <styled.TileDate>{date.getUTCDate()}</styled.TileDate>
            <styled.TileCount empty={!items.length}>{items.length || "—"}</styled.TileCount>
          </styled.DayTile>
        ))}
      </styled.DayStrip>

      {unmarked.length > 0 && (
        <styled.Alert>
          ⚠️ Пробний уже пройшов, а результат не відмічено: <b>{unmarked.length}</b>
          <styled.AlertHint>
            У цих пробних досі стоїть «⏱️ Запланований». Натисніть на учня — відкриється його день — і поставте, як пройшло: відбувся, не зʼявився, скасований або зайшов на навчання. Без цього пробний не потрапляє в статистику.
          </styled.AlertHint>
          <styled.AlertList>
            {unmarked.slice(0, 8).map(({ key, date, time, lesson }) => (
              <styled.AlertItem key={key} onClick={() => jumpTo(date)}>
                {shortDate(ymdToDate(date))} {time} · {lesson.name} (ID {lesson.studentId})
              </styled.AlertItem>
            ))}
            {unmarked.length > 8 && <span>і ще {unmarked.length - 8}</span>}
          </styled.AlertList>
        </styled.Alert>
      )}

      {week.map(({ i, date, ymd, items }) => (
        <styled.DaySection key={ymd} id={`trial-day-${ymd}`} isPast={ymd < today.ymd}>
          <styled.DayHeader isToday={ymd === today.ymd}>
            {ymd === today.ymd ? "Сьогодні · " : ""}{dayLong[i]}, {shortDate(date)}
            <styled.DayCount>{items.length}</styled.DayCount>
          </styled.DayHeader>

          {items.length === 0 ? (
            <styled.EmptyDay>Пробних немає</styled.EmptyDay>
          ) : (
            <styled.Cards>
              {items.map(({ key, time, lesson }) => {
                const removed = lesson.teacherId === "removed";
                const current = status(lesson);
                // tokens щоночі очищаються, тож постійна ознака — lastSeenAt (є з 11.2026); tokens — лише «сьогодні»
                const onPlatform = Boolean(lesson.lastSeenAt) || (lesson.tokens || []).length > 0;
                const seenUnknown = !onPlatform && (lesson.createdAt || "").slice(0, 10) < SEEN_TRACKED_SINCE;
                const seenText = lesson.lastSeenAt ? ` · ${shortDate(ymdToDate(kyivParts(new Date(lesson.lastSeenAt)).ymd))}` : "";
                return (
                  <styled.Card key={key} removed={removed} accent={getStatusColor(lesson.lessonStatus || "scheduled")}>
                    <styled.Time>{time || "—"}</styled.Time>
                    <styled.Body>
                      <styled.Name>
                        {lesson.name} <styled.Muted>ID {lesson.studentId}</styled.Muted>
                        {isAll && <styled.Lang>{langOf(lesson.accessType)?.value || lesson.accessType}</styled.Lang>}
                      </styled.Name>
                      <styled.Meta>
                        <FaChalkboardTeacher />
                        {lesson.teacherName} <styled.Muted>ID {lesson.teacherSchoolId}</styled.Muted>
                        {lesson.ageGroup && <styled.Tag>{lesson.ageGroup}</styled.Tag>}
                        {lesson.englishLevel && <styled.Tag>{lesson.englishLevel}</styled.Tag>}
                        {removed && <styled.Tag>вилучено з розкладу</styled.Tag>}
                      </styled.Meta>
                      <styled.Meta>
                        {trialContacts(lesson).map(({ text, href, fromBot }) => (
                          <styled.Phone key={href} href={href}>📞 {text}{fromBot ? " (з бота)" : ""}</styled.Phone>
                        ))}
                        <styled.Flag ok={Boolean(lesson.botDeliveredAt)}>
                          {lesson.botDeliveredAt ? "✅ Бот підключено" : "⚪ Бот не підключено"}
                        </styled.Flag>
                        <styled.Flag ok={onPlatform}>
                          {onPlatform ? `🟢 Заходив на платформу${seenText}` : seenUnknown ? "Вхід на платформу: немає даних" : "⚪ На платформу не заходив"}
                        </styled.Flag>
                      </styled.Meta>
                      {lesson.notes && (
                        <styled.Note><FaStickyNote /> {lesson.notes}</styled.Note>
                      )}
                    </styled.Body>
                    <styled.Status>
                      {isAll ? (
                        <styled.StatusBadge color={getStatusColor(lesson.lessonStatus || "scheduled")}>
                          {current?.label}
                        </styled.StatusBadge>
                      ) : (
                        <styled.StatusSelect
                          value={lesson.lessonStatus || "scheduled"}
                          onChange={(e) => onStatusChange(lesson._id, e.target.value)}
                          style={{ borderLeft: `4px solid ${getStatusColor(lesson.lessonStatus || "scheduled")}` }}
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </styled.StatusSelect>
                      )}
                      {!isAll && !lesson.botDeliveredAt && !removed && (
                        <BotLinkButton kind="trial" id={lesson._id} done={false}
                          hint="Скопіювати посилання: людина відкриє бота, поділиться номером (тим самим, що в записі) і отримає логін і новий пароль" />
                      )}
                    </styled.Status>
                  </styled.Card>
                );
              })}
            </styled.Cards>
          )}
        </styled.DaySection>
      ))}
    </styled.Wrapper>
  );
}

export default TrialDays;
